import { SearchX } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { ROUTES } from "@/app/routes";
import { BrandContext, canOpen, type BrandScopeState } from "@/core/brand/brand-context";
import { useMyBrands } from "@/core/brand/my-brands";
import { FullScreen } from "@/features/auth/RequireSignIn";
import { SimpleLayout } from "@/layouts/SimpleLayout";
import { Spinner } from "@/ui/spinner";
import { EmptyState, ErrorState } from "@/ui/states";

/**
 * The brand in the address (`/b/:brandId/…`), if this person is in its team: its pages get the brand and
 * their role through `useBrand()`. Someone else's brand looks missing, as it does to the API.
 */
export function BrandScope({ children }: { children: ReactNode }) {
  const { brandId } = useParams();
  const brands = useMyBrands();
  const mine = brands.data?.find((b) => b.brand.id === brandId);
  const value = useMemo<BrandScopeState | null>(
    () => (mine ? { brand: mine.brand, role: mine.role, can: (area) => canOpen(mine.role, area) } : null),
    [mine],
  );

  if (brands.isPending) {
    return (
      <FullScreen>
        <Spinner label="Loading your brand…" />
      </FullScreen>
    );
  }
  if (brands.isError) {
    return (
      <SimpleLayout>
        <ErrorState error={brands.error} onRetry={() => void brands.refetch()} />
      </SimpleLayout>
    );
  }
  if (!value) {
    return (
      <SimpleLayout>
        <EmptyState
          icon={SearchX}
          title="This brand isn't one of yours"
          body="You may have left its team, or the link is wrong."
          action={
            <Link to={ROUTES.start} className="font-bold">
              Go to your brands
            </Link>
          }
        />
      </SimpleLayout>
    );
  }
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
